import React, { useMemo } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Alert, Button } from 'react-bootstrap';
import DataTable from './components/DataTable';
import Loading from './components/Loading';
import useAdminData from './useAdminData';
import { formatMoney } from './format';

const COLUMNS = [
  { key: 'id', label: 'Item #', sortable: true },
  { key: 'title', label: 'Title', sortable: true },
  { key: 'category', label: 'Category', sortable: true, value: item => [item.category, item.subCategory].filter(Boolean).join(' / ') },
  { key: 'price', label: 'Price', sortable: true, value: item => Number(item.price), render: item => formatMoney(item.price) },
  { key: 'inventory', label: 'Qty', sortable: true },
  { key: 'consignerName', label: 'Consigner', sortable: true }
];

/** Items with their consigner's name, for searching and sorting */
export function withConsignerNames(items, consigners) {
  const names = new Map((consigners || []).map(c => [c.id, `${c.lastName}, ${c.firstName}`]));
  return (items || []).map(item => ({ ...item, consignerName: names.get(item.consignerId) || `#${item.consignerId}` }));
}

export default function ItemList() {
  const history = useHistory();
  const location = useLocation();
  const items = useAdminData('/items');
  const consigners = useAdminData('/admin/consigners');

  const params = new URLSearchParams(location.search);
  const consignerId = Number(params.get('consigner')) || null;

  const rows = useMemo(
    () => withConsignerNames(items.data, consigners.data)
      .filter(item => !consignerId || item.consignerId === consignerId),
    [items.data, consigners.data, consignerId]
  );

  const showAllConsigners = () => {
    const next = new URLSearchParams(location.search);
    next.delete('consigner');
    history.replace({ search: next.toString() });
  };

  const consigner = consignerId && (consigners.data || []).find(c => c.id === consignerId);
  const loading = items.loading || consigners.loading;
  const error = items.error || consigners.error;
  const reload = () => {
    items.reload();
    consigners.reload();
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <h2 className="mb-0">Items</h2>
        <Button variant="outline-secondary" size="sm" className="ml-auto" onClick={reload} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && !items.data && <Loading waking={items.waking || consigners.waking} />}

      {consignerId && (
        <Alert variant="info" className="d-flex align-items-center py-2">
          <span>
            Showing items for{' '}
            {consigner
              ? <Link to={`/admin/consigners/${consignerId}`}>{consigner.firstName} {consigner.lastName}</Link>
              : `consigner #${consignerId}`}
          </span>
          <Button variant="link" size="sm" className="ml-auto" onClick={showAllConsigners}>Show all consigners</Button>
        </Alert>
      )}

      {items.data && (
        <DataTable
          columns={COLUMNS}
          rows={rows}
          searchable
          searchPlaceholder="Search by item number, title, category, or consigner"
          onRowClick={item => history.push(`/admin/items/${item.id}`)}
          emptyMessage="No items match"
          showCount
        />
      )}
    </div>
  );
}
